import { useCallback, useId, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';

import { bold, InlineText, TimelineEvent } from '../components/shared';
import { TIMELINE_DESIGN_TOKENS } from '../design-tokens';
import type { WebhookOutcomePayload } from '../types';
import type { EventComponentProps } from './types';
import { webhookOutcomeReason } from './webhookOutcomeCopy';

export function WebhookFailedEvent({ event }: EventComponentProps) {
  const responseId = useId();
  const payload = event.payload as WebhookOutcomePayload | undefined;
  const reason = webhookOutcomeReason(payload?.last_error);
  const [showResponse, setShowResponse] = useState(false);
  const toggleResponse = useCallback(() => setShowResponse(prev => !prev), []);
  const snippet = payload?.last_response_snippet?.trim();
  const snippetMarkdown = useMemo(
    () => (snippet ? `\`\`\`\n${snippet}\n\`\`\`` : undefined),
    [snippet]
  );

  const metaParts: string[] = [];
  if (Number.isFinite(payload?.last_status)) {
    metaParts.push(`HTTP ${payload?.last_status}`);
  }
  if (Number.isFinite(payload?.attempt_count)) {
    metaParts.push(`${payload?.attempt_count} attempt(s)`);
  }
  const meta = metaParts.length > 0 ? metaParts.join(' · ') : undefined;

  const ariaLabel = reason ? `Webhook failed – ${reason}` : 'Webhook failed';

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={
          <InlineText>
            {bold('Webhook failed')}
            {reason ? <> – {reason}</> : null}
          </InlineText>
        }
        timestamp={event.occurred_at}
      >
        {meta && (
          <p
            className={`${TIMELINE_DESIGN_TOKENS.typography.subItem} text-fg-tertiary mt-1`}
          >
            {meta}
          </p>
        )}
        {snippetMarkdown && (
          <div id={responseId} className='mt-2'>
            <Button
              type='button'
              color='tertiary'
              onClick={toggleResponse}
              aria-expanded={showResponse}
              aria-controls={responseId}
              className={`${TIMELINE_DESIGN_TOKENS.typography.subItem} p-0 text-fg-tertiary hover:bg-transparent hover:text-fg-secondary`}
            >
              {showResponse ? 'Hide response' : 'Show response'}
            </Button>
            {showResponse && (
              <MarkdownContent
                content={snippetMarkdown}
                size='compact'
                className='mt-2'
              />
            )}
          </div>
        )}
      </TimelineEvent.Content>
    </TimelineEvent>
  );
}
